import React, { useState, useEffect } from 'react'
import { API, ViewHeader, Card, Help } from '@enkla-gui'
import Table from '@enkla-gui/components/tools/Table'
import { notify } from '@/_helpers'
import { icons } from '@config'
import { Switch } from 'antd'

export default function EntityTypeSettings() {
  const [entityTypes, setEntityTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState({})

  useEffect(() => {
    fetchEntityTypes()
  }, [])

  const fetchEntityTypes = () => {
    setLoading(true)
    API.get('api/billable/entity-types')
      .then(data => {
        setEntityTypes(data)
        setLoading(false)
      })
      .catch(([err, res]) => {
        notify.toast('Kunde inte hämta objektstyper', 'error')
        setLoading(false)
      })
  }

  const handleToggle = async (entityType, checked) => {
    if (!checked && entityType.entity_count > 0) {
      notify.toast(`Kan inte inaktivera ${entityType.name} - det finns ${entityType.entity_count} objekt av denna typ`, 'warning')
      return
    }

    setToggling(prev => ({ ...prev, [entityType.id]: true }))

    try {
      if (checked) {
        await API.post(`api/billable/entity-types/${entityType.id}`)
        notify.toast(`${entityType.name} har aktiverats`)
      } else {
        await API.delete(`api/billable/entity-types/${entityType.id}`)
        notify.toast(`${entityType.name} har inaktiverats`)
      }
      setEntityTypes(prev =>
        prev.map(et =>
          et.id === entityType.id ? { ...et, enabled: checked } : et
        )
      )
    } catch ([err, res]) {
      notify.toast(res?.message || 'Ett fel uppstod', 'error')
    } finally {
      setToggling(prev => ({ ...prev, [entityType.id]: false }))
    }
  }

  const getTypeDescription = (type) => {
    const parts = []
    if (type.rentable) parts.push('Uthyrningsbar')
    if (parts.length === 0) parts.push('Organisationell')
    return parts.join(', ')
  }

  return (
    <>
      <ViewHeader
        icon={icons.entity}
        title='Inställningar'
        subtitle='Objektstyper'
      />
      <Help type='info'>
        <span>
          Här kan du se alla objektstyper. Valfria objektstyper kan aktiveras eller inaktiveras.
          Objektstyper som används kan inte inaktiveras.
        </span>
      </Help>
      <Card loading={loading}
        sorting={{ 'sort[by]': undefined, 'sort[order]': 'asc' }}>
        {entityTypes.length === 0 && !loading ? (
          <div className='text-center text-muted py-4'>
            <i className='fas fa-inbox fa-2x mb-2' />
            <p>Inga objektstyper tillgängliga</p>
          </div>
        ) : (
          <Table
            rows={entityTypes}
            loading={loading}
            sorting={{ 'sort[by]': undefined, 'sort[order]': 'asc' }}
            keys={['_this>function:renderName', '_this>function:renderTypeDesc', '_this>function:renderCount', '_this>function:renderStatus']}
            headers={[
              { key: 'name', value: 'Objektstyp', class: 'min' },
              { key: 'type', value: 'Typbeskrivning' },
              { key: 'count', value: 'Objekt' },
              { key: 'status', value: 'Status', class: 'min' }
            ]}
            functions={{
              renderName: entityType => (
                <div class='icon-badge'>
                  <i className={`fas ${entityType.icon || 'fa-cube'}`} />
                  <span>{entityType.name}</span>
                </div>
              ),
              renderTypeDesc: entityType => (
                getTypeDescription(entityType)
              ),
              renderCount: entityType => (
                entityType.entity_count > 0 ? (
                  <span className='text-muted'>{entityType.entity_count}</span>
                ) : (
                  <span className='text-muted'>-</span>
                )
              ),
              renderStatus: entityType => {
                const isDefault = entityType.is_default
                const isToggling = toggling[entityType.id]

                if (isDefault) {
                  return (
                    <span className='badge bg-success'>
                      <i className='fas fa-check-circle text-success me-1' />
                      Standard
                    </span>
                  )
                }
                return (
                  <Switch
                    checked={entityType.enabled}
                    loading={isToggling}
                    disabled={!entityType.can_disable && entityType.enabled}
                    onChange={(checked) => handleToggle(entityType, checked)}
                    size='small'
                  />
                )
              }
            }}
            empty={{
              text: 'Inga objektstyper tillgängliga',
              icon: 'fa-inbox'
            }}
          />
        )}
      </Card>
    </>
  )
}
